import { NOT_MEASURED, agoLabel, relativeTime, shortDuration } from '../../utils/format'
import type { WorkerActiveRun, WorkerNode } from '../../types/observatory'

/**
 * Presentation for the worker fleet.
 *
 * Every helper here is a wording decision, not a calculation. The endpoint already
 * derives liveness, per-server utilization, the fleet aggregate and the restart
 * fold, and each of those has a long `<remarks>` block recording the bug a
 * client-side "correction" would reintroduce. Nothing in this file recomputes any
 * of them.
 *
 * The one exception is {@link absenceLabel}, and it is not an aggregate: it is the
 * subtraction `WorkerNodeDto.PreviousIncarnationLastSeenAt` exists to make
 * possible, per its own documentation.
 *
 * `whenLabel` and `agoLabel` were born here and now live in `utils/format.ts`.
 * They say nothing about workers — every operational readout in the app wants
 * them — and leaving them here made the second page that needed them import out
 * of a sibling feature's folder. Import them from `utils/format`; do not
 * reintroduce a local copy.
 */

/**
 * "7 of 28 slots busy".
 *
 * The Overview panel keeps these two numbers in a `title` attribute and shows only
 * the percentage, which is why a 1-of-2 host and a 14-of-28 host both read "50%"
 * there. On a fleet page the counts are the point: they are the difference between
 * a small box at its limit and a large one with room.
 */
export function slotsLabel(busy: number, total: number): string {
  if (total === 0) return 'No slots configured'
  return `${busy} of ${total} slot${total === 1 ? '' : 's'} busy`
}

/**
 * Uptime, or a dash.
 *
 * Null exactly when `startedAt` is null — the server computes `now - startedAt` at
 * request time — so a dash here means the incarnation never reported when it
 * began, not that it has been up for no time.
 */
export function uptimeLabel(seconds: number | null): string {
  return seconds === null ? NOT_MEASURED : shortDuration(seconds)
}

/**
 * The restart ledger in one sentence, or null when this row superseded nothing.
 *
 * Both halves matter and neither substitutes for the other: the count says how
 * churny this host has been, the timestamp says whether the churn is happening
 * now or happened on Tuesday.
 */
export function restartHeadline(count: number, previousLastSeenAt: string | null): string | null {
  if (count <= 0) return null
  const times = `Restarted ${count} time${count === 1 ? '' : 's'}`
  return previousLastSeenAt === null
    ? times
    : `${times} — the incarnation before this one was last heard from ${relativeTime(previousLastSeenAt)}`
}

/**
 * How long the host was actually absent across its most recent restart.
 *
 * This is the subtraction the DTO documents as the reason the timestamp is carried
 * out of the fold at all: a rolling redeploy and a four-hour outage nobody noticed
 * produce the same restart count and completely different gaps.
 *
 * A non-positive gap is not an error — it means the replacement was already
 * running when the previous incarnation stopped beating, which is exactly what an
 * overlapped App Service recycle looks like, and it is the good case.
 */
export function absenceLabel(startedAt: string | null, previousLastSeenAt: string | null): string {
  if (previousLastSeenAt === null || startedAt === null) return NOT_MEASURED
  const gapSeconds = (new Date(startedAt).getTime() - new Date(previousLastSeenAt).getTime()) / 1000
  if (Number.isNaN(gapSeconds)) return NOT_MEASURED
  if (gapSeconds <= 0) return 'None — the replacement was already running'
  return shortDuration(gapSeconds)
}

/** Why a process's running list is a dash: the table it is read from does not exist in this database. */
export const ACTIVE_RUNS_UNMEASURED =
  'Which runs this process is performing is not recorded in this database (migration '
  + 'AddSignalHandlingAndRunCancellation not applied).'

/** A measured "performing nothing" — distinct from {@link ACTIVE_RUNS_UNMEASURED}. */
export const ACTIVE_RUNS_NONE = 'Performing no ETL run.'

export type ActiveRunsView =
  | { kind: 'unmeasured'; text: string }
  | { kind: 'none'; text: string }
  | { kind: 'runs'; runs: WorkerActiveRun[] }

/**
 * What a process card's "Running now" list shows.
 *
 * `activeRuns: null` (or absent, from an older API) is NOT MEASURED — the execution table does not exist — and is
 * never rendered as an empty list: an unmeasured process reading as an idle one is exactly the confident fiction this
 * page refuses elsewhere. Only a served `[]` says "performing nothing".
 */
export function activeRunsView(worker: Pick<WorkerNode, 'activeRuns'>): ActiveRunsView {
  const runs = worker.activeRuns
  if (runs == null) return { kind: 'unmeasured', text: ACTIVE_RUNS_UNMEASURED }
  if (runs.length === 0) return { kind: 'none', text: ACTIVE_RUNS_NONE }
  return { kind: 'runs', runs }
}

/**
 * One running line's caption: "Resumed 3m ago · Hangfire job 4711". The job is the one performing the run NOW, which
 * a resume makes different from the run's creating job — so it is said, not left to be assumed.
 */
export function activeRunCaption(run: WorkerActiveRun): string {
  return `${activeRunAttachedText(run)} · Hangfire job ${run.hangfireJobId ?? NOT_MEASURED}`
}

/** When this perform attached: "Started 3m ago", or "Resumed 3m ago" when it reopened an earlier run. */
export function activeRunAttachedText(run: Pick<WorkerActiveRun, 'isResume' | 'attachedAt'>): string {
  return `${run.isResume ? 'Resumed' : 'Started'} ${agoLabel(run.attachedAt)}`
}
